// Types for the public booking site (encrypted schema)
export type ISODateTime = string;
export type ISODate = string; // "YYYY-MM-DD"
export type ConfirmationMode = 'instant' | 'selectClients' | 'selectGroups' | 'request_to_book';
export type BookingRequestStatus = 'pending' | 'confirmed' | 'processed' | 'cancelled' | 'pending_payment';
export type FirestoreDateValue = string | { seconds: number; nanoseconds: number };

/** Entry in listing `socials` array (public profiles). */
export interface ListingSocialLink {
  platform: string;
  value: string;
}

/**
 * Keys for `BusinessListing.sections` (Firestore `sections` on the listing).
 * Navbar and footer are always shown and are not configurable here.
 */
export type WebsiteSectionId =
  | 'hero'
  | 'trust'
  | 'services'
  | 'about'
  | 'reviews'
  | 'booking_cta'
  | 'faq'
  | 'location';

// Public Business Listing (from Firebase)
export interface BusinessListing {
  id: string;
  user_id: string;
  subdomain: string;
  custom_domain?: string;
  business_name: string;
  tagline?: string;
  about?: string;
  timezone: string;
  public_phone?: string;
  public_email?: string;
  logo_url?: string;
  cover_images?: string[];
  cover_image_urls?: string[];
  // Website colors (website-basics-colors spec); read through useSiteColors
  background_color?: string;
  primary_color?: string;
  accent_color?: string;
  published: boolean;
  business_hours?: Record<string, { open?: string; close?: string; closed?: boolean }>;
  booking_window_days?: number;
  min_notice_hours?: number;
  max_bookings_per_day?: number;
  confirmation_mode?: ConfirmationMode;
  cancellation_policy_hours?: number;
  cancellation_policy_text?: string;
  address?: string;
  google_place_id?: string;
  socials?: ListingSocialLink[];
  created_at: string;
  updated_at: string;
  // ── Layout ──────────────────────────────────────────────────────
  hero_layout?: 'split' | 'fullbleed' | 'centered';
  // ── Hero copy ───────────────────────────────────────────────────
  hero_headline?: string;
  hero_subtext?: string;
  cta_label?: string;
  // ── Content ─────────────────────────────────────────────────────
  about_photo_url?: string;
  trust_stats?: Array<{ value: string; label: string }>;
  faq?: Array<{ question: string; answer: string }>;
  /**
   * Optional visibility and order for main page sections (not navbar/footer).
   * Absent or `{}`: theme default order, all listed sections for that theme visible.
   * Non-empty map: only keys set to `true` or a finite number are shown (`true` = default slot).
   * Missing keys, `false`, or `null` hide that section.
   * Use `location` for the contact + location/map block; legacy `contact` is still read as an alias.
   */
  sections?: Partial<Record<WebsiteSectionId, boolean | number | null>>;
  // Deposit / online booking settings (mirrored from users/{uid}/stripe/settings)
  deposits_enabled?: boolean;
  deposit_percent?: number;
  deposit_policy_text?: string;
  booking_site_enabled?: boolean;
}

// Public Service (from Firebase `services/catalog` array entries)
export interface PublicService {
  id: string;
  listing_id?: string;
  name: string;
  description?: string;
  category?: string;
  duration_minutes: number;
  price_cents: number;
  currency: string;
  visible: boolean;
  bookable_online: boolean;
  featured: boolean;
  sort_order: number;
  image_url?: string;
  image_storage_path?: string;
  created_at?: string;
  updated_at?: string;
}

// Booking Request (stored in Firestore by createBooking Cloud Function)
export interface BookingRequest {
  id: string;
  listing_id: string;
  service_id: string;
  /** Client details encrypted to the owner's key. */
  client_data?: {
    algorithm: string;
    ciphertextB64: string;
    nonceB64: string;
    ephemeralPublicKeyB64: string;
  };
  /** The older format, on requests without client_data. */
  encrypted_client_data?: string; // base64
  encryption_iv?: string; // base64
  requested_start_time: FirestoreDateValue;
  requested_end_time: FirestoreDateValue;
  requested_timezone: string;
  status: BookingRequestStatus;
  processed_at?: FirestoreDateValue;
  created_at: FirestoreDateValue;
  // Deposit / hold fields (pending_payment flow)
  hold_expires_at?: FirestoreDateValue | null;
  deposit_payment_id?: string | null;
  source?: 'booking_site' | null;
}

export interface DepositSettings {
  enabled: boolean;
  percent: number;
  policyText: string;
}

// Form data types
export interface CustomerInfo {
  firstName: string;
  lastName: string;
  email?: string;
  phone: string; // Local digits
  countryCode: string; // ISO code (e.g. 'US')
  notes?: string;
}

export interface BookingFormData {
  serviceId: string;
  date: ISODate;
  time: string; // "HH:MM"
  customerInfo: CustomerInfo;
}

// Time slot for availability display
export interface TimeSlot {
  time: string; // "HH:MM" format
  available: boolean;
}

// Utility function to format price from cents
export function formatPrice(cents: number, currency: string = 'USD'): string {
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(dollars);
}

// Public Review (from Firebase `reviews/published` array entries)
export interface PublicReview {
  /** Synthetic id for list keys when reviews have no Firestore document id */
  id: string;
  rating: number;
  review_text?: string;
  reviewer_name: string;
  reviewer_initials?: string;
  verified?: boolean;
}

/** Public location (subcollection `locations` under a listing). */
export interface PublicLocation {
  id: string;
  name: string;
  sort_order?: number;
  google_place_id?: string;
  address_primary?: string;
  address_secondary?: string;
  address_city?: string;
  address_state?: string;
  address_zip_code?: string;
  address_country?: string;
  arrival_instructions?: string;
}
